import { Button } from "@progress/kendo-react-buttons";
import { Notification } from "@progress/kendo-react-notification";
import React from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";
import { URLs } from "@/configs/menu";
import loggerClient from "@/libs/LoggerClient";
import "./style.scss";

export const isChunkLoadError = (error: unknown) => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const err = error as { name?: string; message?: string };
  const message = err.message || "";

  return (
    err.name === "ChunkLoadError" ||
    message.includes("ChunkLoadError") ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("dynamically imported module")
  );
};

export const LazyLoadErrorFallback: React.FC = () => {
  const error = useRouteError() as Error;

  if (!isChunkLoadError(error)) {
    return <ErrorElement />;
  }

  return <LazyLoadErrorFallbackView />;
};

export const appendVersionToUrl = (url: string) => {
  return (
    url +
    (url.includes("?")
      ? "&__VERSION__=" + Date.now()
      : "?__VERSION__=" + Date.now())
  );
};

const LazyLoadErrorFallbackView: React.FC = () => {
  const { t } = useTranslation();
  const resolvedLandingPath = URLs.dashboard;
  const error = useRouteError() as Error;

  React.useEffect(() => {
    loggerClient.recordEvent("UNABLE_TO_LOAD_PAGE", { error: error.message });
  }, [error]);

  return (
    <div className="tlx-lazy-load">
      <div className="tlx-lazy-load__frame" role="alert">
        <div className="tlx-lazy-load__body">
          <div className="tlx-lazy-load__icon-card" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="50"
              height="50"
              viewBox="0 0 50 50"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.1407 27.8519C21.7785 26.2879 21.7785 23.7121 20.1407 22.1481L19.9169 21.9346C15.7187 17.9278 12.4757 14.8338 11.4981 8.34857C11.1522 6.0618 13.1114 4.16666 15.4694 4.16666H25H34.5308C36.8887 4.16666 38.8479 6.0618 38.5021 8.34857C37.5246 14.8338 34.2814 17.9278 30.0833 21.9346L29.8596 22.1481C28.2216 23.7121 28.2216 26.2879 29.8596 27.8519L30.0833 28.0654C34.2814 32.0723 37.5246 35.1662 38.5021 41.6515C38.8479 43.9381 36.8887 45.8333 34.5308 45.8333H25H15.4694C13.1114 45.8333 11.1522 43.9381 11.4981 41.6515C12.4757 35.1662 15.7187 32.0723 19.9169 28.0654L20.1407 27.8519ZM21.5637 31.3885L21.5485 31.4017C18.3493 34.2623 15.9689 36.3919 15.6586 41.659C15.6393 41.9831 15.7797 42.274 16.0086 42.4667C16.1866 42.6175 16.4195 42.7083 16.6739 42.7083H25H33.3252C33.6254 42.7083 33.896 42.5817 34.0821 42.3796C34.256 42.1912 34.3568 41.9375 34.3404 41.659C34.0302 36.3919 31.6498 34.2623 28.4516 31.4017C27.6052 30.645 26.7019 29.8371 25.765 28.9065C25.3429 28.4871 24.6562 28.4871 24.2341 28.9065C23.3023 29.8317 22.4041 30.6358 21.5637 31.3885Z"
                fill="var(--tlx-color-neutral-white-800)"
              />
            </svg>
          </div>
          <h2 className="tlx-lazy-load__title">{t("lazyLoadError.title")}</h2>
          <p className="tlx-lazy-load__message">
            {t("lazyLoadError.messageLine1")}
          </p>
          <p className="tlx-lazy-load__message">
            {t("lazyLoadError.messageLine2")}
          </p>
          <div className="tlx-lazy-load__actions">
            <Button
              size="medium"
              type="button"
              themeColor="success"
              onClick={() =>
                (window.location.href = appendVersionToUrl(
                  window.location.href,
                ))
              }
              data-test-id="lazy-load-retry"
              className="tlx-lazy-load__retry"
            >
              {t("lazyLoadError.retry")}
            </Button>
            <Button
              size="medium"
              type="button"
              fillMode="link"
              onClick={() =>
                (window.location.href = appendVersionToUrl(resolvedLandingPath))
              }
              data-test-id="lazy-load-go-home"
              className="tlx-lazy-load__go-home"
            >
              {t("lazyLoadError.goHome")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ErrorElement: React.FC = () => {
  const error = useRouteError() as Error;
  const { t } = useTranslation();
  const resolvedLandingPath = URLs.dashboard;

  React.useEffect(() => {
    loggerClient.recordError(error as Error);
  }, [error]);

  return (
    <div className="tlx-lazy-load">
      <div className="tlx-lazy-load__frame" role="alert">
        <div className="tlx-lazy-load__body">
          <p className="tlx-lazy-load__message" style={{ maxWidth: "62.5rem" }}>
            <p
              style={{
                fontWeight: 600,
                marginBottom: "0.5rem",
                fontSize: "var(--tlx-font-size-18)",
              }}
            >
              Something went wrong:
            </p>
            <Notification
              type={{ style: "error" }}
              style={{
                marginBottom: "1rem",
                textAlign: "left",
                padding: "1.5rem",
              }}
            >
              <div>
                <pre
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "var(--tlx-font-size-14)",
                    overflow: "auto",
                  }}
                >
                  {error?.message}
                </pre>
                <pre
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "var(--tlx-font-size-12)",
                    overflow: "auto",
                  }}
                >
                  {error?.stack}
                </pre>
              </div>
            </Notification>
          </p>
          <div
            className="tlx-lazy-load__actions"
            style={{ flexDirection: "row" }}
          >
            <Button
              size="medium"
              type="button"
              themeColor="success"
              onClick={() => window.location.reload()}
              data-test-id="lazy-load-retry"
              className="tlx-lazy-load__retry"
            >
              Retry
            </Button>
            <Button
              size="medium"
              type="button"
              fillMode="link"
              onClick={() => (window.location.href = resolvedLandingPath)}
              data-test-id="lazy-load-go-home"
              className="tlx-lazy-load__go-home"
            >
              {t("lazyLoadError.goHome")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LazyLoadErrorFallback;
